import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, MailWarning, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import '@/index.admin.css';
import AdminPwaHead from '@/components/admin/AdminPwaHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminVerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { isAuthenticated, setUser } = useAuth();
  const [status, setStatus] = useState(token ? 'loading' : 'invalid');
  const [message, setMessage] = useState(
    token ? 'Confirming your email…' : 'This verification link is missing or invalid.',
  );
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/admin/verify-email`, { params: { token } })
      .then((res) => {
        if (cancelled) return;
        setStatus('ok');
        setMessage(res.data?.message || 'Email address updated');
        setNewEmail(res.data?.user?.email || '');
        if (res.data?.user) setUser(res.data.user);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('invalid');
        setMessage(err.response?.data?.detail || 'This verification link is invalid or has already been used.');
      });
    return () => {
      cancelled = true;
    };
  }, [token, setUser]);

  return (
    <div className="admin-shell min-h-[100dvh] bg-[#050505] flex items-center justify-center px-4 relative overflow-hidden">
      <AdminPwaHead />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent" />
      <button
        type="button"
        onClick={() => navigate(isAuthenticated ? '/admin/profile' : '/admin/login')}
        className="absolute top-[max(1.5rem,env(safe-area-inset-top))] left-[max(1rem,env(safe-area-inset-left))] min-h-11 text-white/60 hover:text-white transition-colors flex items-center gap-2 px-2"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-['Manrope']">{isAuthenticated ? 'Back to Profile' : 'Go to Login'}</span>
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 border border-blue-500/20 mb-4">
            {status === 'ok' ? (
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            ) : (
              <MailWarning className="w-8 h-8 text-amber-400" />
            )}
          </div>
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
            {status === 'ok' ? 'Email confirmed' : status === 'loading' ? 'Verifying…' : 'Link not valid'}
          </h1>
          <p className="text-white/60 font-['Manrope'] text-sm">
            {message}
            {status === 'ok' && newEmail ? ` You can now sign in as ${newEmail}.` : ''}
          </p>
          {status !== 'loading' && (
            <button
              type="button"
              onClick={() => navigate(isAuthenticated ? '/admin/profile' : '/admin/login')}
              className="mt-6 w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold"
            >
              {isAuthenticated ? 'Return to Profile' : 'Go to Login'}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
